import { useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { signup } from "../api/auth";
import { useAuth } from "../hooks/useAuth";
import { AnimatedButton } from "../components/ui/AnimatedButton";
import { PageTransition } from "../components/ui/PageTransition";

export function SignupPage(): ReactNode {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        Loading...
      </div>
    );
  }

  if (user !== null) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError("");
    setFieldErrors({});

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);

    try {
      await signup(displayName, email, password);
      navigate("/login?registered=1", { replace: true });
    } catch (err) {
      if (err instanceof Error) {
        // Check if it's a validation error with field details
        try {
          const parsed = JSON.parse(err.message) as {
            detail?: Array<{ loc: string[]; msg: string }>;
          };
          if (Array.isArray(parsed.detail)) {
            const errors: Record<string, string[]> = {};
            for (const item of parsed.detail) {
              const field = item.loc[item.loc.length - 1] ?? "unknown";
              if (!errors[field]) errors[field] = [];
              errors[field]?.push(item.msg);
            }
            setFieldErrors(errors);
            return;
          }
        } catch {
          // Not a JSON error — use the message
        }
        setError(err.message);
      } else {
        setError("Signup failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-page">
      <PageTransition>
        <div className="auth-card">
          <div className="auth-logo">prime</div>
          <p className="auth-subtitle">Create your account</p>

          <form onSubmit={(e) => void handleSubmit(e)} noValidate>
            <AnimatePresence>
              {error !== "" && (
                <motion.div
                  role="alert"
                  aria-live="polite"
                  className="auth-error"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <label htmlFor="signup-display-name">Full name</label>
            <input
              id="signup-display-name"
              type="text"
              autoComplete="name"
              required
              placeholder="Your full name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            {fieldErrors["display_name"] && (
              <div role="alert" className="field-error">
                {fieldErrors["display_name"].join(", ")}
              </div>
            )}

            <label htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {fieldErrors["email"] && (
              <div role="alert" className="field-error">
                {fieldErrors["email"].join(", ")}
              </div>
            )}

            <label htmlFor="signup-password">Password</label>
            <input
              id="signup-password"
              type="password"
              autoComplete="new-password"
              required
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {fieldErrors["password"] && (
              <div role="alert" className="field-error">
                {fieldErrors["password"].join(", ")}
              </div>
            )}

            <label htmlFor="signup-confirm-password">Confirm password</label>
            <input
              id="signup-confirm-password"
              type="password"
              autoComplete="new-password"
              required
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <p className="auth-legal">
              prime uses AI to categorize your transactions and provide bookkeeping insights. Your
              financial data is processed by AI systems to deliver the service. You can review how
              your data is handled in our <a href="/privacy">Privacy Policy</a>.
            </p>

            <label className="consent-row">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                I agree to the <a href="/terms">Terms of Service</a> and{" "}
                <a href="/privacy">Privacy Policy</a>, including AI processing of my financial data.
              </span>
            </label>

            <AnimatedButton type="submit" variant="primary" disabled={submitting || !consent}>
              {submitting ? "Creating account..." : "Create account"}
            </AnimatedButton>
          </form>

          <p className="auth-links">
            Already have an account? <Link to="/login">Log in</Link>
          </p>
        </div>
      </PageTransition>
    </main>
  );
}
